import {defineStore} from 'pinia';
import {ref, computed} from 'vue';
import posthog from 'posthog-js';

export interface CookieCategory {
	id: string;
	name: string;
	description: string;
	required: boolean;
	enabled: boolean;
}

export interface CookieConsentState {
	hasConsented: boolean;
	showBanner: boolean;
	showSettings: boolean;
	categories: CookieCategory[];
	lastUpdated: Date | null;
}

export const useCookieConsentStore = defineStore('cookieConsent', () => {
	// State
	const hasConsented = ref(false);
	const showBanner = ref(false);
	const showSettings = ref(false);
	const lastUpdated = ref<Date | null>(null);

	// Cookie categories
	const categories = ref<CookieCategory[]>([
		{
			id: 'necessary',
			name: 'Necessary',
			description:
				'These cookies are essential for the website to function properly. They cannot be disabled.',
			required: true,
			enabled: true,
		},
		{
			id: 'analytics',
			name: 'Analytics',
			description:
				'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
			required: false,
			enabled: false,
		},
		{
			id: 'functional',
			name: 'Functional',
			description:
				'These cookies enable enhanced functionality and personalization, such as remembering your preferences.',
			required: false,
			enabled: false,
		},
		{
			id: 'marketing',
			name: 'Marketing',
			description:
				'These cookies are used to track visitors across websites to display relevant and engaging advertisements.',
			required: false,
			enabled: false,
		},
	]);

	// Ensure necessary cookies are always enabled
	const ensureNecessaryEnabled = () => {
		const necessaryCategory = categories.value.find(
			(c) => c.id === 'necessary'
		);
		if (necessaryCategory && !necessaryCategory.enabled) {
			necessaryCategory.enabled = true;
		}
	};

	// Computed
	const analyticsEnabled = computed(
		() =>
			categories.value.find((c) => c.id === 'analytics')?.enabled || false
	);

	const functionalEnabled = computed(
		() =>
			categories.value.find((c) => c.id === 'functional')?.enabled ||
			false
	);

	const marketingEnabled = computed(
		() =>
			categories.value.find((c) => c.id === 'marketing')?.enabled || false
	);

	// Cookie management
	const COOKIE_NAME = 'cookie_consent';
	const COOKIE_EXPIRY_DAYS = 365;

	const setCookie = (name: string, value: string, days: number) => {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		const expires = `expires=${date.toUTCString()}`;
		document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
	};

	const getCookie = (name: string): string | null => {
		const nameEQ = `${name}=`;
		const ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') c = c.substring(1, c.length);
			if (c.indexOf(nameEQ) === 0)
				return c.substring(nameEQ.length, c.length);
		}
		return null;
	};

	const deleteCookie = (name: string) => {
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
	}; // Load consent from cookie
	const loadConsent = () => {
		// Always ensure necessary cookies are enabled first
		ensureNecessaryEnabled();

		const consentData = getCookie(COOKIE_NAME);
		if (consentData) {
			try {
				const parsed = JSON.parse(decodeURIComponent(consentData));
				hasConsented.value = true;
				showBanner.value = false;
				lastUpdated.value = new Date(parsed.lastUpdated);

				// Update categories
				if (parsed.categories) {
					categories.value.forEach((category) => {
						const savedCategory = parsed.categories.find(
							(c: CookieCategory) => c.id === category.id
						);
						if (savedCategory) {
							// Always enable necessary cookies, respect saved state for others
							if (category.required) {
								category.enabled = true;
							} else {
								category.enabled = savedCategory.enabled;
							}
						}
					});
				}

				// Apply analytics settings
				applyAnalyticsSettings();
			} catch (error) {
				console.error('Error parsing cookie consent data:', error);
				showBanner.value = true;
			}
		} else {
			showBanner.value = true;
		}

		// Final check to ensure necessary are enabled
		ensureNecessaryEnabled();
	};
	// Save consent to cookie
	const saveConsent = () => {
		// Ensure necessary cookies are enabled before saving
		ensureNecessaryEnabled();

		const consentData = {
			hasConsented: true,
			categories: categories.value.map((c) => ({
				id: c.id,
				enabled: c.enabled,
			})),
			lastUpdated: new Date().toISOString(),
		};

		setCookie(
			COOKIE_NAME,
			encodeURIComponent(JSON.stringify(consentData)),
			COOKIE_EXPIRY_DAYS
		);
		hasConsented.value = true;
		showBanner.value = false;
		showSettings.value = false;
		lastUpdated.value = new Date();

		applyAnalyticsSettings();
	};

	// Apply analytics settings based on consent
	const applyAnalyticsSettings = () => {
		if (typeof window === 'undefined') return;

		// PostHog management
		if (analyticsEnabled.value) {
			// Enable PostHog if not already initialized in production
			if (!['debug', 'local'].includes(import.meta.env.VITE_ENV)) {
				if (!posthog.__loaded) {
					posthog.init(
						'phc_34ZoZpoRKredxIvpMuvzMhYVS8y7ouHG9NBqL8cfVal',
						{
							api_host: 'https://eu.i.posthog.com',
							person_profiles: 'identified_only',
						}
					);
				} else {
					posthog.opt_in_capturing();
				}
			}
		} else {
			// Disable PostHog
			if (posthog.__loaded) {
				posthog.opt_out_capturing();
			}
		}

		// Sentry management
		if (analyticsEnabled.value) {
			// Sentry is already initialized, just ensure it's not disabled
			// You might want to add specific Sentry consent methods here
		} else {
			// Disable Sentry data collection
			// Note: Sentry doesn't have a built-in opt-out method after initialization
			// You might need to implement custom logic or reinitialize with different settings
		}
	};
	// Accept all cookies
	const acceptAll = () => {
		categories.value.forEach((category) => {
			category.enabled = true; // Enable all categories including necessary
		});
		saveConsent();
	};

	// Accept only necessary cookies
	const acceptNecessary = () => {
		categories.value.forEach((category) => {
			if (category.required) {
				category.enabled = true; // Keep necessary cookies enabled
			} else {
				category.enabled = false; // Disable optional cookies
			}
		});
		saveConsent();
	};

	// Toggle category
	const toggleCategory = (categoryId: string) => {
		const category = categories.value.find((c) => c.id === categoryId);
		if (category && !category.required) {
			category.enabled = !category.enabled;
		}
	};

	// Show settings modal
	const openSettings = () => {
		showSettings.value = true;
	};

	// Close settings modal
	const closeSettings = () => {
		showSettings.value = false;
	};
	// Reset consent (useful for development/testing)
	const resetConsent = () => {
		deleteCookie(COOKIE_NAME);
		hasConsented.value = false;
		showBanner.value = true;
		showSettings.value = false;
		lastUpdated.value = null;
		categories.value.forEach((category) => {
			if (category.required) {
				category.enabled = true; // Keep necessary cookies enabled
			} else {
				category.enabled = false; // Reset optional cookies to disabled
			}
		});
	};

	// Check if consent is expired (optional - for periodic re-consent)
	const isConsentExpired = computed(() => {
		if (!lastUpdated.value) return true;
		const daysSinceUpdate =
			(Date.now() - lastUpdated.value.getTime()) / (1000 * 60 * 60 * 24);
		return daysSinceUpdate > COOKIE_EXPIRY_DAYS;
	});
	return {
		// State
		hasConsented,
		showBanner,
		showSettings,
		categories,
		lastUpdated,

		// Computed
		analyticsEnabled,
		functionalEnabled,
		marketingEnabled,
		isConsentExpired,

		// Actions
		loadConsent,
		saveConsent,
		acceptAll,
		acceptNecessary,
		toggleCategory,
		openSettings,
		closeSettings,
		resetConsent,
		applyAnalyticsSettings,
		ensureNecessaryEnabled,
	};
});
